import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subject,
  map,
  shareReplay,
  takeUntil,
} from 'rxjs';
import { AuthService } from '@adminprodigydesign/axis';
import { AccountService, AddressSummary } from '../../services/account.service';
import {
  CheckoutService,
  OrderStatusResult,
} from '../../services/checkout.service';
import { AnalyticsService } from '../../services/analytics.service';
import {
  ACCOUNT_SECTIONS,
  CHANGE_PASSWORD_INPUTS,
  UPDATE_NAME_INPUTS,
  UPDATE_PHONE_INPUTS,
} from './my-account.config';
import {
  AccountOrder,
  AccountOrderHistoryItem,
  AccountSection,
  AddressFeedbackOptions,
  ChangePasswordPayload,
  MyAccountVm,
  OrderDetailModalData,
  UpdateNamePayload,
  UpdatePhonePayload,
} from './my-account.types';
import {
  isValidAccountSection,
  mapAccountOrder,
  mapAddressSummary,
  mapOrderDetails,
  mapUserVm,
} from './my-account.utils';

@Component({
  selector: 'app-my-account',
  standalone: false,
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.scss'],
})
export class MyAccountComponent implements OnInit, OnDestroy {
  readonly sections = ACCOUNT_SECTIONS;
  readonly changePasswordInputs = CHANGE_PASSWORD_INPUTS;

  activeSection: AccountSection = 'profile';
  vm$: Observable<MyAccountVm>;
  private profileRefreshSubject = new BehaviorSubject<void>(undefined);

  private destroy$ = new Subject<void>();

  private addressesLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly addressesLoading$ = this.addressesLoadingSubject.asObservable();

  private ordersLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly ordersLoading$ = this.ordersLoadingSubject.asObservable();

  readonly updateNameInputs = UPDATE_NAME_INPUTS;
  readonly updatePhoneInputs = UPDATE_PHONE_INPUTS;

  addressesLoaded = false;
  addressesError = '';
  addresses: AddressSummary[] = [];

  ordersLoaded = false;
  ordersError = '';
  orders: AccountOrder[] = [];

  addressDeletingId: string | null = null;
  addressDefaultingId: string | null = null;
  addressActionMessage = '';
  addressActionError = '';

  showDeleteAddressModal = false;
  pendingDeleteAddress: AddressSummary | null = null;

  passwordSaving = false;
  passwordSuccessMsg = '';
  passwordErrorMsg = '';
  showChangePasswordModal = false;

  showViewOrderModal = false;
  viewOrderLoading = false;
  viewOrderError = '';
  selectedOrderDetails: OrderDetailModalData | null = null;

  profileSaving = false;
  profileSuccessMsg = '';
  profileErrorMsg = '';

  showEditNameModal = false;
  showEditPhoneModal = false;

  profileNameOverride: { fName?: string; lName?: string } | null = null;
  profilePhoneOverride: string | null = null;

  constructor(
    private accountService: AccountService,
    private authService: AuthService,
    private checkoutService: CheckoutService,
    private router: Router,
    private route: ActivatedRoute,
    private analyticsService: AnalyticsService,
  ) {
    this.vm$ = this.authService.user$.pipe(map(mapUserVm), shareReplay(1));
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const sectionParam = params.get('section');

        if (!isValidAccountSection(sectionParam)) {
          this.navigateToSection('profile', true);
          return;
        }

        this.activeSection = sectionParam;

        this.trackAccountEvent('account_section_view', {
          section: sectionParam,
        });

        this.loadActiveSectionData();
      });
  }

  setSection(section: AccountSection): void {
    if (this.activeSection === section) return;

    this.passwordSuccessMsg = '';
    this.passwordErrorMsg = '';
    this.navigateToSection(section);
  }

  logout(): void {
    this.trackAccountEvent('account_logout');
    this.authService.logout().subscribe();
  }

  loadAddresses(options?: AddressFeedbackOptions): void {
    this.addressesLoadingSubject.next(true);
    this.addressesError = '';

    if (!options?.preserveFeedback) {
      this.clearAddressFeedback();
    }

    this.accountService
      .viewAddresses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const rawAddresses = Array.isArray(response?.data)
            ? response.data
            : [];
          this.addresses = rawAddresses.map(mapAddressSummary);
          this.addressesLoaded = true;
          this.addressesLoadingSubject.next(false);

          this.trackAccountEvent('account_addresses_loaded', {
            address_count: this.addresses.length,
          });
        },
        error: (err) => {
          console.error('[MyAccount] viewAddress error', err);
          this.addressesError =
            'We could not load your saved addresses right now.';
          this.addressesLoaded = true;
          this.addressesLoadingSubject.next(false);
        },
      });
  }

  refreshAddresses(options?: AddressFeedbackOptions): void {
    this.trackAccountEvent('account_addresses_refresh');
    this.addressesLoaded = false;
    this.loadAddresses(options);
  }

  openDeleteAddressModal(address: AddressSummary): void {
    this.clearAddressFeedback();
    this.pendingDeleteAddress = address;
    this.showDeleteAddressModal = true;

    this.trackAccountEvent('account_address_delete_open', {
      is_default: !!address?.isDefault,
    });
  }

  closeDeleteAddressModal(): void {
    if (this.isDeleteModalBusy) return;
    this.showDeleteAddressModal = false;
    this.pendingDeleteAddress = null;
  }

  confirmDeleteAddress(): void {
    const address = this.pendingDeleteAddress;
    const linkId = address?.userAddressLinkId;

    if (!address || linkId == null || linkId === '') {
      this.addressActionError = 'We could not delete this address.';
      this.addressActionMessage = '';
      this.showDeleteAddressModal = false;
      this.pendingDeleteAddress = null;
      return;
    }

    this.addressDeletingId = String(linkId);
    this.clearAddressFeedback();

    this.accountService
      .deleteAddressLink(linkId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.addressDeletingId = null;

          if (response?.status === 'success') {
            this.showDeleteAddressModal = false;
            this.pendingDeleteAddress = null;
            this.addressActionMessage =
              response?.message || 'Address deleted successfully.';

            this.trackAccountEvent('account_address_deleted', {
              was_default: !!address?.isDefault,
            });

            this.refreshAddresses({ preserveFeedback: true });
            this.clearAddressMessageLater();
            return;
          }

          this.addressActionError =
            response?.message || 'We could not delete this address.';
        },
        error: (err) => {
          console.error('[MyAccount] deleteAddressLink error', err);
          this.addressDeletingId = null;
          this.addressActionError =
            err?.error?.message ||
            err?.message ||
            'We could not delete this address.';
        },
      });
  }

  setDefaultAddress(address: AddressSummary): void {
    const linkId = address?.userAddressLinkId;

    if (address?.isDefault) return;

    if (linkId == null || linkId === '') {
      this.addressActionError = 'We could not update the default address.';
      this.addressActionMessage = '';
      return;
    }

    this.addressDefaultingId = String(linkId);
    this.clearAddressFeedback();

    this.accountService
      .setDefaultAddress(linkId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.addressDefaultingId = null;

          if (response?.status === 'success') {
            this.addressActionMessage =
              response?.message || 'Default address updated successfully.';

            this.trackAccountEvent('account_address_set_default');

            this.refreshAddresses({ preserveFeedback: true });
            this.clearAddressMessageLater();
            return;
          }

          this.addressActionError =
            response?.message || 'We could not update the default address.';
        },
        error: (err) => {
          console.error('[MyAccount] updateAddressLink error', err);
          this.addressDefaultingId = null;
          this.addressActionError =
            err?.error?.message ||
            err?.message ||
            'We could not update the default address.';
        },
      });
  }

  loadOrders(): void {
    this.ordersLoadingSubject.next(true);
    this.ordersError = '';

    this.accountService
      .viewUserOrderHistory()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const orderHistory: AccountOrderHistoryItem[] = Array.isArray(
            response?.orderHistory,
          )
            ? response.orderHistory
            : [];

          const visibleOrderHistory = orderHistory.filter(
            (order: AccountOrderHistoryItem) =>
              this.shouldShowOrderInHistory(order),
          );

          this.orders = visibleOrderHistory.map(mapAccountOrder);
          this.ordersLoaded = true;
          this.ordersLoadingSubject.next(false);

          this.trackAccountEvent('account_orders_loaded', {
            order_count: this.orders.length,
            hidden_payment_intent_count:
              orderHistory.length - visibleOrderHistory.length,
          });
        },
        error: (err) => {
          console.error('[MyAccount] viewUserOrderHistory error', err);
          this.ordersError = 'We could not load your orders right now.';
          this.ordersLoaded = true;
          this.ordersLoadingSubject.next(false);
        },
      });
  }

  trackOrder(order: AccountOrder): void {
    const orderId = Number(order.id);

    if (!Number.isFinite(orderId)) {
      console.warn('[MyAccount] Cannot track order: invalid order id', order);
      return;
    }

    this.trackAccountEvent('account_order_track', {
      has_order_number: !!order.orderNumber,
      status: order.status ?? '',
      fulfilment_type: order.fulfilmentType ?? '',
    });

    this.checkoutService.getOrderStatus(orderId).subscribe({
      next: (result: OrderStatusResult) => {
        try {
          sessionStorage.setItem('lastOrderId', String(orderId));
          sessionStorage.setItem(
            'lastOrderCode',
            result.publicOrderCode || order.orderNumber || '',
          );
          sessionStorage.setItem(
            'trackedOrderStatus',
            JSON.stringify({
              orderId,
              publicOrderCode: result.publicOrderCode,
              uiStatus: result.uiStatus,
              backendStatusRaw: result.backendStatusRaw,
              paymentStatus: result.paymentStatus ?? '',
              createdAt: result.createdAt ?? '',
              createdAtLocal: result.createdAtLocal ?? '',
              updatedAt: result.updatedAt ?? '',
              updatedAtLocal: result.updatedAtLocal ?? '',
              orderType: result.orderType ?? '',
            }),
          );
        } catch (err) {
          console.warn(
            '[MyAccount] Failed to persist tracked order state',
            err,
          );
        }

        this.router.navigate(['/order-confirmed'], {
          state: {
            trackedFromAccount: true,
            orderId,
            publicOrderCode: result.publicOrderCode,
            status: result,
          },
        });
      },
      error: (err) => {
        console.error('[MyAccount] trackOrder error', err);
        this.ordersError =
          'We could not track this order right now. Please try again.';
      },
    });
  }

  viewOrder(order: AccountOrder): void {
    const orderId = Number(order.id);

    if (!Number.isFinite(orderId)) {
      console.warn('[MyAccount] Cannot view order: invalid order id', order);
      return;
    }

    this.trackAccountEvent('account_order_view_details', {
      has_order_number: !!order.orderNumber,
      status: order.status ?? '',
      fulfilment_type: order.fulfilmentType ?? '',
    });

    this.viewOrderLoading = true;
    this.viewOrderError = '';
    this.selectedOrderDetails = null;
    this.showViewOrderModal = true;

    this.accountService.viewUserOrderDetails(orderId).subscribe({
      next: (response) => {
        this.viewOrderLoading = false;
        const details = mapOrderDetails(response, order);

        if (!details) {
          this.viewOrderError = 'We could not load this order.';
          return;
        }

        this.selectedOrderDetails = details;
      },
      error: (err) => {
        console.error('[MyAccount] viewUserOrderDetails error', err);
        this.viewOrderLoading = false;
        this.viewOrderError = 'We could not load this order right now.';
      },
    });
  }

  closeViewOrderModal(): void {
    if (this.viewOrderLoading) return;
    this.showViewOrderModal = false;
    this.viewOrderError = '';
    this.selectedOrderDetails = null;
  }

  openChangePasswordModal(): void {
    this.passwordSuccessMsg = '';
    this.passwordErrorMsg = '';
    this.showChangePasswordModal = true;

    this.trackAccountEvent('account_password_modal_open');
  }

  closeChangePasswordModal(): void {
    if (this.passwordSaving) return;
    this.showChangePasswordModal = false;
  }

  submitChangePassword = (payload: ChangePasswordPayload): void => {
    this.passwordSaving = true;
    this.passwordSuccessMsg = '';
    this.passwordErrorMsg = '';

    const requestPayload = {
      action: 'updatePass',
      oldPassword: String(payload?.oldPassword || '').trim(),
      password: String(payload?.password || '').trim(),
    };

    this.authService.authenticate('updatePass', requestPayload).subscribe({
      next: (response: any) => {
        this.passwordSaving = false;

        if (response?.status === 'success') {
          this.passwordSuccessMsg =
            response?.message || 'Your password has been updated successfully.';
          this.passwordErrorMsg = '';

          this.trackAccountEvent('account_password_updated');

          setTimeout(() => {
            this.showChangePasswordModal = false;
            this.passwordSuccessMsg = '';
          }, 1200);

          return;
        }

        this.passwordErrorMsg =
          response?.message || 'We could not update your password.';
      },
      error: (err) => {
        console.error('[MyAccount] updatePass error', err);
        this.passwordSaving = false;
        this.passwordSuccessMsg = '';
        this.passwordErrorMsg =
          err?.error?.message ||
          err?.message ||
          'We could not update your password.';
      },
    });
  };

  canTrackOrder(order: AccountOrder): boolean {
    return Number.isFinite(Number(order?.id));
  }

  isDefaultingAddress(address: AddressSummary): boolean {
    return (
      this.addressDefaultingId === String(address?.userAddressLinkId ?? '')
    );
  }

  isAddressBusy(address: AddressSummary): boolean {
    const linkId = String(address?.userAddressLinkId ?? '');
    return (
      this.addressDeletingId === linkId || this.addressDefaultingId === linkId
    );
  }

  get isDeleteModalBusy(): boolean {
    if (!this.pendingDeleteAddress?.userAddressLinkId) return false;
    return (
      this.addressDeletingId ===
      String(this.pendingDeleteAddress.userAddressLinkId)
    );
  }

  trackByAddress(index: number, address: AddressSummary): string {
    return String(address.userAddressLinkId ?? address.id ?? index);
  }

  trackByOrder(index: number, order: AccountOrder): string {
    return order.orderNumber || `${index}`;
  }

  getDisplayFullName(vm: MyAccountVm): string {
    const first = this.profileNameOverride?.fName ?? vm.user?.fName ?? '';
    const last = this.profileNameOverride?.lName ?? vm.user?.lName ?? '';
    return `${String(first).trim()} ${String(last).trim()}`.trim();
  }

  getDisplayPhone(vm: MyAccountVm): string {
    return this.profilePhoneOverride ?? vm.user?.phone ?? '';
  }

  openEditNameModal(): void {
    this.profileSuccessMsg = '';
    this.profileErrorMsg = '';
    this.showEditNameModal = true;

    this.trackAccountEvent('account_name_modal_open');
  }

  closeEditNameModal(): void {
    if (this.profileSaving) return;
    this.showEditNameModal = false;
  }

  openEditPhoneModal(): void {
    this.profileSuccessMsg = '';
    this.profileErrorMsg = '';
    this.showEditPhoneModal = true;

    this.trackAccountEvent('account_phone_modal_open');
  }

  closeEditPhoneModal(): void {
    if (this.profileSaving) return;
    this.showEditPhoneModal = false;
  }

  submitUpdateName = (payload: UpdateNamePayload): void => {
    const firstName = String(payload?.firstName || '').trim();
    const lastName = String(payload?.lastName || '').trim();

    this.profileSaving = true;
    this.profileSuccessMsg = '';
    this.profileErrorMsg = '';

    this.accountService
      .updateNameInfo(firstName, lastName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.profileSaving = false;

          if (response?.status === 'success') {
            this.profileNameOverride = {
              fName: firstName,
              lName: lastName,
            };

            this.profileSuccessMsg =
              response?.message || 'Your name has been updated successfully.';
            this.profileErrorMsg = '';

            this.trackAccountEvent('account_name_updated');

            setTimeout(() => {
              this.showEditNameModal = false;
              this.profileSuccessMsg = '';
            }, 1200);

            return;
          }

          this.profileErrorMsg =
            response?.message || 'We could not update your name.';
        },
        error: (err) => {
          console.error('[MyAccount] updateNameInfo error', err);
          this.profileSaving = false;
          this.profileSuccessMsg = '';
          this.profileErrorMsg =
            err?.error?.message ||
            err?.message ||
            'We could not update your name.';
        },
      });
  };

  submitUpdatePhone = (payload: UpdatePhonePayload): void => {
    const phoneNumber = String(payload?.phoneNumber || '').trim();

    this.profileSaving = true;
    this.profileSuccessMsg = '';
    this.profileErrorMsg = '';

    this.accountService
      .updateContactPhone(phoneNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.profileSaving = false;

          if (response?.status === 'success') {
            this.profilePhoneOverride = phoneNumber;

            this.profileSuccessMsg =
              response?.message ||
              'Your mobile number has been updated successfully.';
            this.profileErrorMsg = '';

            this.trackAccountEvent('account_phone_updated');

            setTimeout(() => {
              this.showEditPhoneModal = false;
              this.profileSuccessMsg = '';
            }, 1200);

            return;
          }

          this.profileErrorMsg =
            response?.message || 'We could not update your mobile number.';
        },
        error: (err) => {
          console.error('[MyAccount] updateContactPhone error', err);
          this.profileSaving = false;
          this.profileSuccessMsg = '';
          this.profileErrorMsg =
            err?.error?.message ||
            err?.message ||
            'We could not update your mobile number.';
        },
      });
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private navigateToSection(section: AccountSection, replaceUrl = false): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { section },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private loadActiveSectionData(): void {
    if (
      this.activeSection === 'addresses' &&
      !this.addressesLoaded &&
      !this.addressesLoadingSubject.value
    ) {
      this.loadAddresses();
    }

    if (
      this.activeSection === 'orders' &&
      !this.ordersLoaded &&
      !this.ordersLoadingSubject.value
    ) {
      this.loadOrders();
    }
  }

  private clearAddressFeedback(): void {
    this.addressActionMessage = '';
    this.addressActionError = '';
  }

  private clearAddressMessageLater(): void {
    setTimeout(() => {
      this.addressActionMessage = '';
    }, 2500);
  }

  private trackAccountEvent(
    eventName: string,
    params?: Record<string, string | number | boolean | null | undefined>,
  ): void {
    try {
      this.analyticsService.trackEvent(eventName, {
        page_area: 'account',
        ...params,
      });
    } catch (err) {
      console.warn('[MyAccount] analytics tracking failed', err);
    }
  }

  private shouldShowOrderInHistory(order: AccountOrderHistoryItem): boolean {
    const paymentStatus = String(order?.paymentStatus || '')
      .trim()
      .toLowerCase();

    return paymentStatus !== 'payment_intent';
  }
}
